/**
 * Add a building, and edit any building, in eMerge (AUTHORITY RULING, Isaiah
 * Cotto, 2026-10-05: eMerge is the authority for every operational field of
 * every building; Monday is provenance only; "if eMerge lacks an edit path,
 * build it").
 *
 *   AddBuildingButton     "Add a building": a hand-built drawer (no Radix
 *                         Dialog; see pages/admin/bd/bdUi.tsx) with the form.
 *   AddBuildingForm       the form itself, also opened in place from the
 *                         reconciliation report with a Monday item id as
 *                         evidence ("Add in eMerge").
 *   EditBuildingSection   inside a building's row panel: the provenance line
 *                         and, when the caller may, "Edit building" -- the
 *                         address fields and the business, compliance and map
 *                         fields (business status, region, beds,
 *                         credentialing, BAA, coordinates).
 *
 * Both are shown only when GET /scheduling/buildings/meta says so (the write
 * group is open AND the caller holds facility.manage at enterprise scope).
 * A possible duplicate is never added silently: the server's candidates are
 * shown, and adding anyway takes an explicit confirmation that is recorded.
 * When the add carries a Monday item id, a candidate may instead be LINKED to
 * that item (the building eMerge already has). Every save carries an
 * Idempotency-Key (one per form opening) and an edit carries the version it
 * was made against. Nothing here names a resident.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Drawer } from '../admin/bd/bdUi';
import { outcomeOf } from '../finance/financeApi';
import type { ApiResult } from '../finance/financeApi';
import {
  buildingsApi, newBuildingKey, fieldErrorsOf, candidatesOf, Building, BuildingMeta, BuildingField, BuildingType, DuplicateCandidate,
  BUILDING_FIELDS, BUILDING_REQUIRED, BUILDING_LABEL, DetailField, DETAIL_FIELDS, DETAIL_LABEL, FormField,
} from './buildingsApi';

/** What the caller may do with buildings. Loads once; a failure means "nothing", never a guess. */
export function useBuildingMeta(): BuildingMeta | null {
  const [meta, setMeta] = useState<BuildingMeta | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await buildingsApi.meta();
        if (live && r && r.success && r.data) setMeta(r.data);
      } catch { /* no meta: no add or edit is offered; the server refuses anyway */ }
    })();
    return () => { live = false; };
  }, []);
  return meta;
}

/**
 * A write that got no answer may or may not have been applied, so it is never
 * called "nothing was recorded". The same key re-sends safely.
 */
const UNSURE = 'Could not reach the server, so this may or may not have been saved. Try again: the same request will not be applied twice.';
const writeOutcome = (r: ApiResult<unknown>): string => (r.status === undefined ? UNSURE : outcomeOf(r, { wrote: true }));

type Values = Record<FormField, string>;
const EMPTY: Values = {
  name: '', facility_type: '', address: '', city: '', state: '', zip_code: '', county: '', licensed_beds: '', corporation: '', email: '', notes: '',
  status: '', region: '', beds: '', credentialing_status: '', baa_sent: '', baa_sent_date: '', baa_signed: '', baa_signed_date: '', latitude: '', longitude: '',
};
/** Corporation is a list (Monday's is a dropdown); the form shows every value, separated by semicolons. */
const CORP_SEPARATOR = '; ';
const yn = (b: boolean | null | undefined) => (b === true ? 'yes' : b === false ? 'no' : '');
const str = (x: string | number | null | undefined) => (x === null || x === undefined ? '' : String(x));
const valuesOf = (b: Building): Values => ({
  name: b.name || '', facility_type: b.facility_type || '', address: b.address || '', city: b.city || '', state: b.state || '', zip_code: b.zip_code || '',
  county: b.county || '', licensed_beds: b.licensed_beds || '', corporation: (b.corporation || []).join(CORP_SEPARATOR), email: b.email || '', notes: b.notes || '',
  status: str(b.status), region: str(b.region), beds: str(b.beds), credentialing_status: str(b.credentialing_status),
  baa_sent: yn(b.baa_sent), baa_sent_date: str(b.baa_sent_date), baa_signed: yn(b.baa_signed), baa_signed_date: str(b.baa_signed_date),
  latitude: str(b.latitude), longitude: str(b.longitude),
});
const LABEL: Record<FormField, string> = { ...BUILDING_LABEL, ...DETAIL_LABEL };
const EDIT_FIELDS: FormField[] = [...BUILDING_FIELDS, ...DETAIL_FIELDS];
/** Decimal degrees within plus or minus `limit`, at most 8 decimal places (the column's scale). */
const DEG = (limit: number) => (s: string) => /^-?\d{1,3}(\.\d{1,8})?$/.test(s) && Math.abs(Number(s)) <= limit;

/**
 * The checks the server makes, said before the round trip. The server still
 * decides. `only` limits them to the fields being changed: an edit must not be
 * blocked by an old value (an address without a number, beds written as
 * "120 SNF / 30 ALF") in a field the user did not touch.
 */
function check(v: Values, { required, only }: { required: boolean; only?: FormField[] }): Partial<Record<FormField, string>> {
  const e: Partial<Record<FormField, string>> = {};
  const look = (k: FormField) => !only || only.includes(k);
  if (required) for (const k of BUILDING_REQUIRED) if (look(k) && !v[k].trim()) e[k] = `Enter the ${BUILDING_LABEL[k].toLowerCase()}.`;
  if (look('state') && v.state.trim() && !/^[A-Za-z]{2}$/.test(v.state.trim())) e.state = 'Enter the state as two letters, for example FL.';
  if (look('zip_code') && v.zip_code.trim() && !/^\d{5}(-\d{4})?$/.test(v.zip_code.trim())) e.zip_code = 'Enter the ZIP as five digits, for example 33602, or ZIP+4 as 33602-1234.';
  if (look('address') && v.address.trim() && !/^\s*\d/.test(v.address)) e.address = 'Enter the street address with its number, for example 301 South Bay Street.';
  if (look('licensed_beds') && v.licensed_beds.trim() && !/^\d{1,4}$/.test(v.licensed_beds.trim())) e.licensed_beds = 'Enter licensed beds as a whole number, for example 120.';
  if (look('email') && v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = 'Enter the email as name@example.com, or leave it empty.';
  if (look('beds') && v.beds.trim() && (!/^\d{1,4}$/.test(v.beds.trim()) || Number(v.beds.trim()) > 5000)) e.beds = 'Enter beds as a whole number from 0 to 5,000, for example 120.';
  if (look('latitude') && v.latitude.trim() && !DEG(90)(v.latitude.trim())) e.latitude = 'Enter the latitude in decimal degrees between -90 and 90, for example 27.9506.';
  if (look('longitude') && v.longitude.trim() && !DEG(180)(v.longitude.trim())) e.longitude = 'Enter the longitude in decimal degrees between -180 and 180, for example -82.4572.';
  // The pairs: a BAA date needs its yes; coordinates come together. Checked when the request touches the pair.
  for (const [flag, date, what] of [['baa_sent', 'baa_sent_date', 'sent'], ['baa_signed', 'baa_signed_date', 'signed']] as const) {
    if ((look(flag) || look(date)) && v[date].trim() && v[flag] !== 'yes') {
      if (look(date)) e[date] = `A BAA ${what} date needs "BAA ${what}" set to yes.`; else e[flag] = `Clear the BAA ${what} date too, or keep "BAA ${what}" at yes.`;
    }
  }
  if ((look('latitude') || look('longitude')) && !e.latitude && !e.longitude && Boolean(v.latitude.trim()) !== Boolean(v.longitude.trim())) {
    e[v.latitude.trim() ? 'longitude' : 'latitude'] = 'Enter both latitude and longitude, or clear both.';
  }
  return e;
}

const HINT: Partial<Record<FormField, string>> = {
  corporation: 'Several? Separate them with a semicolon.',
  status: 'The building\'s business status, in the TM board\'s words. Business Development reads it as the stage when the account has none of its own. Set by a person; nothing changes it by date.',
  credentialing_status: 'Shown on the Scheduling board. It does not stop scheduling (CRED-RULING-001).',
  latitude: 'Entered by hand, for example 27.9506. A geocoder may suggest it later; nothing fills it automatically.',
  longitude: 'Entered by hand, for example -82.4572. Enter both coordinates, or neither.',
};

type Kind = 'text' | 'type' | 'notes' | 'status' | 'yesno' | 'date' | 'suggest';
const KIND: Partial<Record<FormField, Kind>> = {
  facility_type: 'type', notes: 'notes', status: 'status', baa_sent: 'yesno', baa_signed: 'yesno', baa_sent_date: 'date', baa_signed_date: 'date',
  region: 'suggest', credentialing_status: 'suggest',
};

function Field({ k, value, onChange, error, types, required, options, label: labelOverride }: {
  k: FormField | 'monday_item_id'; value: string; onChange: (v: string) => void; error?: string; types: BuildingType[]; required: boolean; options?: string[]; label?: string;
}) {
  const id = useId();
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  const listId = `${id}-list`;
  const label = `${labelOverride || LABEL[k as FormField]}${required ? '' : ' (optional)'}`;
  const hint = k === 'monday_item_id' ? 'The Monday item this building is, if it is on Monday. eMerge links it to the building; Monday is not changed.' : HINT[k as FormField];
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  const common = { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy, 'aria-required': required || undefined };
  const kind: Kind = KIND[k as FormField] || 'text';
  const opts = options || [];
  // The label names the field and nothing else; the hint and the error sit beside it and are tied by aria-describedby,
  // so a screen reader hears the name once and the problem once.
  return (
    <div className="sc-field">
      <label htmlFor={id}>{label}</label>
      {kind === 'type' ? (
        <select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose a type</option>
          {value && !types.some((t) => t.code.toUpperCase() === value.toUpperCase()) && <option value={value}>{value}</option>}
          {types.map((t) => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
        </select>
      ) : kind === 'status' ? (
        // An old spelling of a listed word ("active") shows as that word; it is only sent if someone changes it.
        <select {...common} value={opts.find((w) => w.toUpperCase() === value.toUpperCase()) ?? value} onChange={(e) => onChange(e.target.value)}>
          <option value="">None</option>
          {/* An old word outside the list stays as it is until someone chooses another. */}
          {value && !opts.some((w) => w.toUpperCase() === value.toUpperCase()) && <option value={value}>{value} (current; not in the list)</option>}
          {opts.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      ) : kind === 'yesno' ? (
        <select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Not recorded</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : kind === 'notes' ? (
        <textarea {...common} value={value} onChange={(e) => onChange(e.target.value)} maxLength={4000} rows={3} />
      ) : (
        <input
          {...common} value={value} onChange={(e) => onChange(e.target.value)}
          maxLength={k === 'state' ? 2 : k === 'zip_code' ? 10 : kind === 'suggest' ? 60 : k === 'monday_item_id' ? 18 : 200}
          inputMode={k === 'zip_code' || k === 'licensed_beds' || k === 'beds' || k === 'monday_item_id' ? 'numeric' : k === 'latitude' || k === 'longitude' ? 'decimal' : undefined}
          type={k === 'email' ? 'email' : kind === 'date' ? 'date' : 'text'}
          list={kind === 'suggest' && opts.length ? listId : undefined}
          autoComplete="off"
        />
      )}
      {kind === 'suggest' && opts.length > 0 && <datalist id={listId}>{opts.map((w) => <option key={w} value={w} />)}</datalist>}
      {hint && <span id={hintId} className="um-quiet sc-field-hint">{hint}</span>}
      {/* Tied to the field by aria-describedby; the form's one summary line is the alert, so a screen reader hears it once. */}
      {error && <span id={errId} className="sc-field-error">{error}</span>}
    </div>
  );
}

/**
 * The duplicate guard's answer: the candidates, and an explicit, recorded
 * "these are different buildings". With a Monday item in hand, a candidate
 * that does not already hold it can be linked to it instead.
 * Keyed by its candidate list where it is used: a new list resets the tick and moves focus again.
 */
function DuplicateStep({ candidates, verb, busy, onConfirm, onBack, linkItem, onLink }: {
  candidates: DuplicateCandidate[]; verb: string; busy: boolean; onConfirm: () => void; onBack: () => void; linkItem?: string | null; onLink?: (c: DuplicateCandidate) => void;
}) {
  const [checked, setChecked] = useState(false);
  const id = useId();
  // The form (and the button that was focused) just went away: move focus here, so it does not fall to the page
  // and a screen reader announces why the form was replaced.
  const headRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => { headRef.current?.focus(); }, []);
  return (
    <div className="um-form" role="group" aria-labelledby={`${id}-h`} aria-describedby={`${id}-d`}>
      <h4 id={`${id}-h`} ref={headRef} tabIndex={-1} style={{ margin: 0 }}>This may already be on file</h4>
      <p id={`${id}-d`} className="um-quiet" style={{ margin: 0 }}>
        eMerge found {candidates.length === 1 ? 'a building' : `${candidates.length} buildings`} that may be this one. Two records for one building stop residents matching to it.
        If it is on this list, go back and open it instead{linkItem && onLink ? `, or link Monday item ${linkItem} to it` : ''}.
      </p>
      <ul aria-label="Possible duplicates">
        {candidates.map((c) => (
          <li key={c.id}>
            <strong>{c.name || 'Name not recorded'}</strong>{c.city ? ` · ${c.city}` : ''} · matched on {c.matched_on.join(', ') || 'not recorded'}
            {c.provenance && <span className="sc-src">{c.provenance}</span>}
            {/* A candidate matched on the Monday item, itself or through a building merged into it ("Monday item (merged)"), already holds it: a link would be refused. */}
            {linkItem && onLink && !c.matched_on.some((m) => m.startsWith('Monday item')) && (
              <button type="button" className="um-open" disabled={busy} onClick={() => onLink(c)}>Link Monday item {linkItem} to {c.name || 'this building'}</button>
            )}
          </li>
        ))}
      </ul>
      <label htmlFor={`${id}-c`} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input id={`${id}-c`} type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>I checked: none of these is this building</span>
      </label>
      <div className="btns">
        <button type="button" className="um-open um-primary" disabled={!checked || busy} onClick={onConfirm}>{busy ? 'Saving…' : `Confirm: ${verb} anyway`}</button>
        <button type="button" className="um-open" onClick={onBack} disabled={busy}>Back to the form</button>
      </div>
      <p className="um-quiet" style={{ margin: 0 }}>Your confirmation is recorded with the building.</p>
    </div>
  );
}
const dupesKey = (d: DuplicateCandidate[]) => d.map((c) => c.id).join(',');

/* ── Add ─────────────────────────────────────────────────────────────── */

export type AddOutcome = 'added' | 'linked';

/**
 * Add a building. `initial` pre-fills it (the reconciliation report passes the
 * Monday item's name and id). A Monday item id is sent as evidence: eMerge
 * links it to the new building, or -- from the duplicate step -- to the
 * building eMerge already has.
 */
export function AddBuildingForm({ meta, onDone, onCancel, initial }: {
  meta: BuildingMeta; onDone: (b: Building, how: AddOutcome) => void; onCancel: () => void; initial?: { name?: string; monday_item_id?: string };
}) {
  const [v, setV] = useState<Values>({ ...EMPTY, name: (initial && initial.name) || '' });
  const [item, setItem] = useState<string>((initial && initial.monday_item_id) || '');
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [dupes, setDupes] = useState<DuplicateCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [key] = useState(newBuildingKey);
  const [linkKey] = useState(newBuildingKey);
  const set = (k: BuildingField) => (x: string) => { setV((cur) => ({ ...cur, [k]: x })); setErrors((cur) => ({ ...cur, [k]: undefined })); };
  const evidence = item.trim();

  const send = async (notDuplicateOf?: string[]) => {
    const local: Partial<Record<string, string>> = check(v, { required: true, only: [...BUILDING_FIELDS] });
    if (evidence && !/^\d{1,18}$/.test(evidence)) local.monday_item_id = 'A Monday item id is digits only.';
    if (Object.keys(local).length) { setErrors(local); setFormErr('Check the fields marked below.'); return; }
    setBusy(true); setFormErr(null);
    try {
      const fields: Partial<Record<BuildingField | 'monday_item_id', string>> = {};
      for (const k of BUILDING_FIELDS) if (v[k].trim()) fields[k] = v[k].trim();
      if (evidence) fields.monday_item_id = evidence;
      const r = await buildingsApi.create(fields, key, notDuplicateOf);
      if (r.success && r.data) { onDone(r.data.building, 'added'); return; }
      if (r.code === 'POSSIBLE_DUPLICATE') { setDupes(candidatesOf(r)); return; }
      setDupes(null);
      setErrors(fieldErrorsOf(r));
      setFormErr(writeOutcome(r));
    } catch { setFormErr(UNSURE); } finally { setBusy(false); }
  };

  /** Link the Monday item to the building eMerge already has (an edit carrying only the item, at its current version). */
  const linkTo = async (c: DuplicateCandidate) => {
    setBusy(true); setFormErr(null);
    try {
      const g = await buildingsApi.get(c.id);
      if (!g.success || !g.data) { setDupes(null); setFormErr(outcomeOf(g)); return; }
      const r = await buildingsApi.update(c.id, { monday_item_id: evidence }, g.data.version, linkKey);
      if (r.success && r.data) { onDone(r.data.building, 'linked'); return; }
      setDupes(null);
      setFormErr(writeOutcome(r));
    } catch { setDupes(null); setFormErr(UNSURE); } finally { setBusy(false); }
  };

  if (dupes) {
    return (
      <DuplicateStep key={dupesKey(dupes)} candidates={dupes} verb="Add the building" busy={busy} onConfirm={() => send(dupes.map((c) => c.id))} onBack={() => setDupes(null)}
        linkItem={evidence || null} onLink={meta.can.edit && evidence ? linkTo : undefined} />
    );
  }
  return (
    <form className="um-form" aria-label="Add a building" noValidate onSubmit={(e) => { e.preventDefault(); send(); }}>
      <p className="um-quiet" style={{ margin: 0 }}>
        Added in eMerge, and eMerge is its authority. It joins the Scheduling board under &ldquo;No open plan&rdquo;. The main phone and the people to call are added from the building&rsquo;s contacts.
      </p>
      {BUILDING_FIELDS.map((k) => (
        <Field key={k} k={k} value={v[k]} onChange={set(k)} error={errors[k]} types={meta.types} required={BUILDING_REQUIRED.includes(k)} />
      ))}
      <Field k="monday_item_id" label="Monday item id" value={item} onChange={(x) => { setItem(x); setErrors((cur) => ({ ...cur, monday_item_id: undefined })); }} error={errors.monday_item_id} types={[]} required={false} />
      {formErr && <p className="um-notice stop" role="alert">{formErr}</p>}
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Adding…' : 'Add the building'}</button>
        <button type="button" className="um-open" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

/** "Add a building", shown only when the server says this caller may. */
export function AddBuildingButton({ meta, onAdded }: { meta: BuildingMeta | null; onAdded: (b: Building) => void }) {
  const [open, setOpen] = useState(false);
  const [round, setRound] = useState(0);
  if (!meta || !meta.can.create) return null;
  return (
    <>
      <button type="button" className="board-btn um-open" onClick={() => { setRound((n) => n + 1); setOpen(true); }}>Add a building</button>
      <Drawer scope="um" open={open} onClose={() => setOpen(false)} title="Add a building" description="eMerge is the authority for every building. Monday is not changed.">
        <div style={{ padding: 16 }}>
          <AddBuildingForm key={round} meta={meta} onCancel={() => setOpen(false)} onDone={(b) => { setOpen(false); onAdded(b); }} />
        </div>
      </Drawer>
    </>
  );
}

/* ── Edit ────────────────────────────────────────────────────────────── */

/** What a changed form value is sent as: the server reads "yes"/"no", numbers and dates as typed, and null to clear. */
const sendAs = (raw: string): string | null => raw.trim() || null;

/**
 * Inside a building's panel: its provenance and, when the caller may edit,
 * "Edit building". Any building, Monday-origin included. Only the fields that
 * change are sent, with the version they were made against.
 */
export function EditBuildingSection({ facilityId, name, meta, onChanged }: { facilityId: string; name: string; meta: BuildingMeta | null; onChanged: (msg: string) => Promise<void> | void }) {
  const canEdit = Boolean(meta && meta.can.edit);
  const [building, setBuilding] = useState<Building | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [dupes, setDupes] = useState<DuplicateCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newBuildingKey);
  const detailsId = useId();
  // When the form closes (saved, cancelled or reloaded) its focused control unmounts; focus goes back to "Edit building".
  const editRef = useRef<HTMLButtonElement | null>(null);
  const [refocus, setRefocus] = useState(false);
  useEffect(() => { if (refocus && !editing) { editRef.current?.focus(); setRefocus(false); } }, [refocus, editing]);
  const close = () => { setEditing(false); setDupes(null); setRefocus(true); };

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const r = await buildingsApi.get(facilityId);
      if (r.success && r.data) { setBuilding(r.data); setV(valuesOf(r.data)); } else { setBuilding(null); setLoadErr(outcomeOf(r)); }
    } catch { setBuilding(null); setLoadErr('Could not reach the server.'); }
  }, [facilityId]);
  useEffect(() => { if (canEdit) load(); }, [canEdit, load]);

  if (!canEdit) return null;
  const set = (k: FormField) => (x: string) => { setV((cur) => ({ ...cur, [k]: x })); setErrors((cur) => ({ ...cur, [k]: undefined })); };
  const start = () => { if (building) setV(valuesOf(building)); setErrors({}); setFormErr(null); setDupes(null); setKey(newBuildingKey()); setEditing(true); };
  const vocab = meta && meta.vocab ? meta.vocab : { status: [], region: [], credentialing_status: [] };
  const optionsOf = (k: FormField): string[] | undefined => (k === 'status' ? vocab.status : k === 'region' ? vocab.region : k === 'credentialing_status' ? vocab.credentialing_status : undefined);

  const save = async (notDuplicateOf?: string[]) => {
    if (!building) return;
    const was = valuesOf(building);
    const changed = EDIT_FIELDS.filter((k) => v[k].trim() !== was[k].trim());
    if (!changed.length) { setFormErr('Nothing has changed.'); return; }
    // Only what changes is checked, as the server does: an old value elsewhere never blocks this edit.
    const local = check(v, { required: false, only: changed });
    for (const k of BUILDING_REQUIRED) if (changed.includes(k) && !v[k].trim() && was[k]) local[k] = `The ${BUILDING_LABEL[k].toLowerCase()} cannot be emptied.`;
    if (Object.keys(local).length) { setErrors(local); setFormErr('Check the fields marked below.'); return; }
    const fields: Partial<Record<FormField, string | null>> = {};
    for (const k of changed) fields[k] = sendAs(v[k]);
    setBusy(true); setFormErr(null);
    try {
      const r = await buildingsApi.update(facilityId, fields, building.version, key, notDuplicateOf);
      if (r.success && r.data) {
        setBuilding(r.data.building); setV(valuesOf(r.data.building)); close();
        await onChanged(`${r.data.building.name}: building details saved.`);
        return;
      }
      if (r.code === 'POSSIBLE_DUPLICATE') { setDupes(candidatesOf(r)); return; }
      setDupes(null);
      setErrors(fieldErrorsOf(r));
      setFormErr(r.code === 'STALE' ? 'Someone else changed this building first. Reload it to see the current details, then make your change again.' : writeOutcome(r));
    } catch { setFormErr(UNSURE); } finally { setBusy(false); }
  };
  const field = (k: FormField) => (
    <Field key={k} k={k} value={v[k]} onChange={set(k)} error={errors[k]} types={meta ? meta.types : []} required={BUILDING_REQUIRED.includes(k as BuildingField)} options={optionsOf(k)} />
  );

  return (
    <div className="sc-building" style={{ gridColumn: '1 / -1' }} role="group" aria-label={`${name}: building details`}>
      <h3>Building details</h3>
      {loadErr && <p className="um-notice stop" role="alert">{loadErr} <button type="button" className="um-open" onClick={load}>Try again</button></p>}
      {!building && !loadErr && <p className="um-quiet" role="status">Loading the building&rsquo;s details…</p>}
      {building && <p className="sc-provenance">{building.provenance.text}.</p>}
      {building && !editing && (
        building.merged
          ? <p className="um-quiet">This building was merged into another one; edit that building instead.</p>
          : <div className="sc-cmds"><button type="button" ref={editRef} className="um-open" onClick={start} aria-label={`Edit building ${name}`}>Edit building</button></div>
      )}
      {building && editing && dupes && (
        <DuplicateStep key={dupesKey(dupes)} candidates={dupes} verb="Save the change" busy={busy} onConfirm={() => save(dupes.map((c) => c.id))} onBack={() => setDupes(null)} />
      )}
      {building && editing && !dupes && (
        <form className="um-form" aria-label={`Edit building ${name}`} noValidate onSubmit={(e) => { e.preventDefault(); save(); }}>
          {BUILDING_FIELDS.map(field)}
          <fieldset className="sc-fieldset" aria-describedby={`${detailsId}-d`}>
            <legend>Business, compliance and map</legend>
            <p id={`${detailsId}-d`} className="um-quiet" style={{ margin: 0 }}>eMerge keeps these now; Monday no longer changes them.</p>
            {DETAIL_FIELDS.map(field)}
          </fieldset>
          {formErr && (
            <p className="um-notice stop" role="alert">
              {formErr}
              {formErr.startsWith('Someone else changed') && <> <button type="button" className="um-open" onClick={async () => { await load(); close(); }}>Reload</button></>}
            </p>
          )}
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Saving…' : 'Save building details'}</button>
            <button type="button" className="um-open" onClick={close} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
